import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";

export async function ItemSlowNoSalesPrint(
  payload,
  { title = "Item Sales Health Report (Slow / No-Sales)" } = {}
) {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const safe = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));
  const num = (v, digits = 0) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "-";
    return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  };

  const params = payload?.params || {};
  const slowItems = Array.isArray(payload?.slowItems) ? payload.slowItems : [];
  const noSalesItems = Array.isArray(payload?.noSalesItems) ? payload.noSalesItems : [];
  const summary = payload?.summary || {};

  const nowText = dayjs().format("YYYY-MM-DD HH:mm");
  const subtitle = `Window: last ${safe(params.months)} months | Slow rule: bottom ${safe(params.slowPercent)}% (sold-in-window only) | Cutoff: ${safe(params.cutoffDate)}`;

  const drawHeader = () => {
    const pageWidth = pdf.internal.pageSize.getWidth();

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text(title, 10, 12);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);
    pdf.setTextColor(107, 114, 128);
    pdf.text(subtitle, 10, 18);

    pdf.text("Generated", pageWidth - 10, 12, { align: "right" });
    pdf.setTextColor(17, 24, 39);
    pdf.setFont("helvetica", "bold");
    pdf.text(nowText, pageWidth - 10, 17, { align: "right" });

    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.4);
    pdf.line(10, 22, pageWidth - 10, 22);

    pdf.setTextColor(17, 24, 39);
  };

  const drawFooter = () => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const pageNumber = pdf.getCurrentPageInfo().pageNumber;
    const pageCount = pdf.getNumberOfPages();

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(156, 163, 175);

    pdf.text(
      "Tip: Review pricing, visibility, and stock levels for slow/no-sales items before reordering.",
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );

    pdf.text(`Page ${pageNumber} / ${pageCount}`, pageWidth - 10, pageHeight - 8, { align: "right" });
    pdf.setTextColor(17, 24, 39);
  };

  // Cover summary block
  drawHeader();
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.text("Summary", 10, 30);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.text(`Total items checked: ${safe(summary.totalItems)}`, 10, 36);
  pdf.text(`Slow-moving items: ${safe(summary.slowCount)}`, 10, 42);
  pdf.text(`No sales in window: ${safe(summary.noSalesCount)}`, 10, 48);

  // ---- Table 1: No Sales
  autoTable(pdf, {
    startY: 56,
    head: [["Item", "Stock", "Sold (window)", "Avg / Month", "Last Sale", "Days Since"]],
    body: noSalesItems.map((r) => [
      safe(r.itemName),
      num(r.currentStock),
      num(r.soldLastMonths),
      num(r.avgMonthlySold, 2),
      safe(r.lastSaleDate),
      safe(r.daysSinceLastSale),
    ]),
    theme: "grid",
    margin: { left: 10, right: 10, top: 26, bottom: 16 },
    headStyles: { fillColor: [249, 250, 251], textColor: [17, 24, 39], fontStyle: "bold" },
    bodyStyles: { fontSize: 10, cellPadding: { top: 2.3, right: 2, bottom: 2.3, left: 2 } },
    didDrawPage: () => {
      drawHeader();
      drawFooter();
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text(`No Sales in Last ${safe(params.months)} Months`, 10, 26);
    },
    columnStyles: {
      0: { halign: "left" },              // Item
      1: { halign: "right", cellWidth: 18 }, // Stock
      2: { halign: "right", cellWidth: 26 }, // Sold
      3: { halign: "right", cellWidth: 24 }, // Avg
      4: { halign: "center", cellWidth: 28 },// Last Sale
      5: { halign: "right", cellWidth: 22 }, // Days
    },
  });

  // ---- Table 2: Slow moving
  autoTable(pdf, {
    startY: pdf.lastAutoTable.finalY + 10,
    head: [["Item", "Stock", "Sold (window)", "Avg / Month", "Last Sale", "Days Since"]],
    body: slowItems.map((r) => [
      safe(r.itemName),
      num(r.currentStock),
      num(r.soldLastMonths),
      num(r.avgMonthlySold, 2),
      safe(r.lastSaleDate),
      safe(r.daysSinceLastSale),
    ]),
    theme: "grid",
    margin: { left: 10, right: 10, top: 26, bottom: 16 },
    headStyles: { fillColor: [249, 250, 251], textColor: [17, 24, 39], fontStyle: "bold" },
    bodyStyles: { fontSize: 10, cellPadding: { top: 2.3, right: 2, bottom: 2.3, left: 2 } },
    didDrawPage: () => {
      drawHeader();
      drawFooter();
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("Slow-Moving Items", 10, 26);
    },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "right", cellWidth: 18 },
      2: { halign: "right", cellWidth: 26 },
      3: { halign: "right", cellWidth: 24 },
      4: { halign: "center", cellWidth: 28 },
      5: { halign: "right", cellWidth: 22 },
    },
  });

  pdf.save("ITEM-SALES-HEALTH.pdf");
}